import AlignBoxMiddleCenter20 from '@carbon/icons-react/es/align-box--middle-center/20';
import Bullhorn20 from '@carbon/icons-react/es/bullhorn/20';
import Document20 from '@carbon/icons-react/es/document/20';
import Roadmap20 from '@carbon/icons-react/es/roadmap/20';
import { Row } from 'antd';
import React, { FC } from 'react';
import { useLocation } from 'react-router-dom';
import { FormTemplate } from 'api/form/types';
import { RoutePaths } from 'constant';
import { Template, TemplateObjectType } from 'data/templates/api';
import { TranslationKey } from 'hooks/use-translation';
import { useNativeActiveOrInvitedUsersQuery } from 'queries/members';
import { RenderFormTemplateCard } from './form-template-card';
import { TemplateListSection } from './list-section';
import { TemplatesWrapper } from './styles';

const TemplateMeta = {
  [TemplateObjectType.Project]: {
    translationKey: 'templates.projects' as TranslationKey,
    objectType: TemplateObjectType.Project,
    route: RoutePaths.projectTemplateRoot,
    icon: <Roadmap20 />,
  },
  [TemplateObjectType.Document]: {
    translationKey: 'templates.documents' as TranslationKey,
    objectType: TemplateObjectType.Document,
    route: RoutePaths.documentTemplateRoot,
    icon: <Document20 />,
  },
  [TemplateObjectType.StatusUpdates]: {
    translationKey: 'templates.status_updates' as TranslationKey,
    objectType: TemplateObjectType.StatusUpdates,
    route: RoutePaths.statusUpdateTemplateRoot,
    icon: <Bullhorn20 />,
  },
  [TemplateObjectType.Form]: {
    translationKey: 'templates.forms' as TranslationKey,
    objectType: TemplateObjectType.Form,
    route: RoutePaths.formTemplateRoot,
    icon: <AlignBoxMiddleCenter20 />,
  },
};

export const TemplatesList: FC<{
  loading: boolean;
  formTemplatesLoading: boolean;
  templates?: Template[];
  formTemplates: FormTemplate[];
  onDelete: (id, objectType: TemplateObjectType) => void;
  onDuplicate: (id, objectType: TemplateObjectType) => void;
}> = ({
  loading,
  templates = [],
  formTemplates,
  formTemplatesLoading,
  onDelete,
  onDuplicate,
}) => {
  const location = useLocation();
  const { data } = useNativeActiveOrInvitedUsersQuery();

  const currentTemplateView = location.pathname;

  return (
    <TemplatesWrapper>
      <Row gutter={[16, 24]}>
        {Object.values(TemplateMeta).map(
          ({ translationKey, objectType, route, icon }) =>
            route === currentTemplateView ? (
              route === RoutePaths.formTemplateRoot ? (
                <TemplateListSection
                  onDelete={onDelete}
                  onDuplicate={onDuplicate}
                  key={objectType}
                  loading={formTemplatesLoading}
                  objectType={objectType}
                  route={route}
                  translationKey={translationKey}
                  icon={icon}
                  templates={formTemplates.map((template) => {
                    return {
                      templateId: template.templateId,
                      templateName: template.name,
                      usageCount: template.usageCount || 0,
                      enableAutoPublish: false,
                      private: false,
                      createdBy: data?.find(
                        (user) => user.userId === template.createdBy,
                      ),
                      createdAt: template.createdAt,
                      publishedBy: data?.find(
                        (user) => user.userId === template.updatedBy,
                      ),
                      publishedAt: template.updatedAt,
                    };
                  })}
                  customTemplateCardRenderer={RenderFormTemplateCard}
                />
              ) : (
                <TemplateListSection
                  onDelete={onDelete}
                  onDuplicate={onDuplicate}
                  loading={loading}
                  objectType={objectType}
                  route={route}
                  icon={icon}
                  translationKey={translationKey}
                  templates={templates
                    .filter((template) => template.objectType === objectType)
                    .map((template) => {
                      return {
                        templateId: template.templateId,
                        templateName: template.templateName || '',
                        enableAutoPublish:
                          template.objectType ===
                            TemplateObjectType.StatusUpdates &&
                          template.enableAutoPublish,
                        private:
                          template.objectType ===
                            TemplateObjectType.StatusUpdates &&
                          template.private,
                        createdBy: template.createdBy,
                        createdAt: template.createdAt,
                        publishedBy: template.publishedBy,
                        publishedAt: template.publishedAt,
                      };
                    })}
                />
              )
            ) : null,
        )}
      </Row>
    </TemplatesWrapper>
  );
};
